/*

* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.
* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.
*/
import { PropsWithChildren, useEffect, useState } from 'react';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import {
  Sidebar,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/MenuOutlined';
import CategoryIcon from '@material-ui/icons/CategoryOutlined';
import { AppHeader } from '../common/appHeader/AppHeader';
import SidebarDivider from '../common/sidebar/SidebarDivider';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { Backdrop } from '@material-ui/core';
import { CookieConsentModal } from '../common/CookieConsentModal';

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const cookieConsentEnabled: boolean = true;

  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const identityApi = useApi(identityApiRef);
   // small helper to determine whether we should show consent
  const needsConsent = () => !localStorage.getItem('cookie-consent-accepted');
  useEffect(() => {
    // If feature disabled, ensure modal hidden and skip further checks.
    if (!cookieConsentEnabled) {
      setShowCookieConsent(false);
      return;
    }

    const checkAuthAndShowCookie = async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        setIsAuthenticated(Boolean(identity));
        if (identity && needsConsent()) {
          setTimeout(() => setShowCookieConsent(true), 1000);
        }
      } catch {
        const hasKeycloakAuth =
          document.cookie.includes('KEYCLOAK_SESSION') ||
          localStorage.getItem('keycloak-token') ||
          sessionStorage.getItem('keycloak-token');
        if (hasKeycloakAuth) {
          setIsAuthenticated(true);
          if (needsConsent()) {
            setTimeout(() => setShowCookieConsent(true), 1500);
          }
        }
      }
    };

    void checkAuthAndShowCookie();
  }, [identityApi, cookieConsentEnabled]);

  const handleCookieConsent = () => {
    localStorage.setItem('cookie-consent-accepted', 'true');
    localStorage.setItem('cookie-consent-timestamp', new Date().toISOString());
    setShowCookieConsent(false);
  };

  return (
    <>
      {/* Main content with backdrop overlay when privacy notice is shown */}
      <div style={{ position: 'relative' }}>
        <SidebarPage>
          <AppHeader />
          <Sidebar>
            <SidebarGroup label="Menu" icon={<MenuIcon />}>
              {/* Global nav, not org-specific */}
              {/* <SidebarItem icon={DashboardIcon} to="/" text="Home" /> */}
              <SidebarItem icon={CategoryIcon} to="catalog" text="Catalog" />
              {/* <SidebarItem
            icon={ExtensionIcon}
            to="api-docs?filters[kind]=api&filters[lifecycles]=production&filters[user]=all"
            text="APIs"
          /> */}
              {/* <SidebarItem
            icon={ExtensionIcon}
            to="/dashboard"
            text="Dashboard"
          /> */}
              {/* <SidebarItem
            icon={LibraryBooks}
            to="docs?filters[user]=all"
            text="Docs"
          /> */}
              {/* Removed - External User*/}
              {/* <SidebarItem
            icon={CreateComponentIcon}
            to="catalog-import"
            text="Register..."
          /> */}
              {/* End global nav */}
              {/* <SidebarDivider /> */}
              {/* Removed - External User*/}
              {/* <SidebarItem icon={ToolsIcon} to="tools" text="Tools" /> */}
              {/* <SidebarItem
            icon={CommunityIcon}
            to="collaboration"
            text="Collaboration"
          /> */}
              {/* <SidebarDivider /> */}
              {/* Removed - External User*/}
              {/* <SidebarItem icon={BuildIcon} to="entity-validation" text="Validator" /> */}
            </SidebarGroup>
            <SidebarSpace />
            <SidebarDivider />
            <SidebarGroup
              label="Settings"
              icon={<UserSettingsSignInAvatar />}
              to="/settings"
            >
              <SidebarSettings />
            </SidebarGroup>
          </Sidebar>
          {children}
        </SidebarPage>
        {showCookieConsent && cookieConsentEnabled && (
          <Backdrop
            open={showCookieConsent}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1200,
            }}
          />
        )}
      </div>
      {/* Privacy Notice Modal - appears on top of everything */}
      {showCookieConsent && isAuthenticated && cookieConsentEnabled && (
        <CookieConsentModal
          open={showCookieConsent}
          onAccept={handleCookieConsent}
        />
      )}
    </>
  );
};
